import { Card, Grid } from "@mui/material";
import { handleDownloadCsv, handlePrintCsv } from "components/utils/handlers";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  executeCommand,
  getSingleDevicesSocket,
  setupConsoleResponseSocket,
  setupDashboardSocket,
  unSubscribeSocketTopics,
} from "../../../redux/saga/socket";
import { applicationsActions } from "../../../redux/slice/applications.slice";
import { authActions } from "../../../redux/slice/auth.slice";
import { devicesActions } from "../../../redux/slice/devices.slice";
import { networksActions } from "../../../redux/slice/networks.slice";
import { packagesActions } from "../../../redux/slice/packages.slice";
import { portsActions } from "../../../redux/slice/ports.slice";
import { processesActions } from "../../../redux/slice/processes.slice";
import { GET_SINGLE_DEVICE, GET_SINGLE_DEVICE_LOCATION } from "../../../redux/types/devices.types";
import { GET_DASHBOARD_ANALYTICS, GET_DEVICE_USERS } from "../../../redux/types/shared.types";
import DeleteIcon from "../../../static/svgs/Delete";
import NoneIcon from "../../../static/svgs/None";
import CustomTitle from "../../shared/CustomTitle";
import MyTable from "../../shared/Tables/MyTable/MyTable";
import MyTableToolbar from "../../shared/Tables/MyTable/MyTableToolbar";
import table from "../../shared/Tables/table.module.scss";
import { commandTypes } from "../../utils";
import { OS, TOPICS } from "../../utils/enums";
import theme from "../../utils/theme";
import { handleDeleteApps } from "../Apps/helper";
import DeviceMap from "../Device/DeviceMap/DeviceMap";
import { getMarker } from "../Device/helper";
import DashboardCards from "./DashboardCards";
import DetailChartCard from "./DetailChartCard";
import SystemInfo from "./SystemInfo";
import {
  getAppDetailItems,
  getPortColumns,
  LINUX_APP_DOWNLOAD_COLS,
  linuxAllAppsColumns,
  NETWORK_DOWNLOAD_COLS,
  networkColumns,
  PORT_DOWNLOAD_COLS,
  PROCESS_DOWNLOAD_COLS,
  processesColumns,
  WINDOWS_APP_DOWNLOAD_COLS,
  windowsAllAppsColumns
} from "./helper";
import { accessActions } from "../../../redux/slice/access.slice";
import CheckSinglePerm from "components/shared/CheckPerm";
import { USER_PERMISSIONS } from "../Management/AccessManagement/helper";
import { checkSinglePermission } from "components/shared/helper";
import toast from "react-hot-toast";

const SingleDashboard = () => {
  const dispatch = useDispatch();
  const combineApps = useSelector(applicationsActions.selectors.getCombineApps);
  const networks = useSelector(networksActions.selectors.getNetworks);
  const ports = useSelector(portsActions.selectors.getPorts);
  const applications = useSelector(applicationsActions.selectors.getApplications);
  const packages = useSelector(packagesActions.selectors.getPackages);
  const processes = useSelector(processesActions.selectors.getProcesses);
  const selectedDevice = useSelector(devicesActions.selectors.getSelectedDevice);
  const singleDeviceLocation = useSelector(devicesActions.selectors.getSingleDeviceLocation);
  const currentUser = useSelector(authActions.selectors.getCurrentUser);
  const userPermissions = useSelector(accessActions.selectors.getUserPermissions)

  useEffect(() => {
    setupDashboardSocket(dispatch);
    setupConsoleResponseSocket(dispatch)
    return () => {
      unSubscribeSocketTopics([
        TOPICS.PROCESSES,
        TOPICS.SYSTEM,
        TOPICS.SYSTEM_PACKAGES,
        TOPICS.APPLICATIONS,
        TOPICS.NETWORK_INTERFACES,
        TOPICS.PORTS,
        TOPICS.ALL_APPS,
        TOPICS.USERS
      ]);
    };
  }, [dispatch]);



  const { id } = useParams();
  useEffect(() => {
    if (id) {
      const payload = {
        deviceId: id,
      };
      getSingleDevicesSocket({ orgId: currentUser?.orgId, _id: id });
      dispatch({ type: GET_DASHBOARD_ANALYTICS, payload });
      dispatch({ type: GET_SINGLE_DEVICE, payload });
      dispatch({ type: GET_DEVICE_USERS, payload });
      dispatch({ type: GET_SINGLE_DEVICE_LOCATION, payload });
    }
  }, [currentUser?.orgId, dispatch, id]);

  const appDetails = useMemo(() => {
    return getAppDetailItems(
      combineApps?.length,
      applications?.length,
      packages?.length
    );
  }, [combineApps?.length, applications?.length, packages?.length]);

  const markers = useMemo(() => {
    if (singleDeviceLocation?.loc) {
      const coordinates = singleDeviceLocation?.loc?.split(",").map(Number).reverse();
      if (coordinates?.length === 2) {
        const html = getMarker(theme.palette.success.main);
        return [{ coordinates, html, device: singleDeviceLocation?.device }];
      }
    }
    return [];
  }, [singleDeviceLocation]);

  const graphColors = [
    theme.palette.success.main,
    theme.palette.error.main,
    theme.palette.warning.light,
  ];

  const [selectedProcesses, setSelectedProcesses] = useState([]);
  const handleProcessesSelection = (ids) => {
    setSelectedProcesses(ids);
  };

  const [selectedPackages, setSelectedPackages] = useState([]);
  const handlePackageSelection = (ids) => {
    setSelectedPackages(ids);
  };

  const handleKillProcesses = () => {
    if (selectedDevice?.status !== "Connected") {
      toast.error("Cannot kill processes: Device is disconnected");
      return;
    }
    const filteredObjects = processes.filter((object) =>
      selectedProcesses.includes(object.id)
    );
    const pids = filteredObjects?.map((item) => item?.pid);

    const toastId = toast.loading('Killing processes...', { duration: 10000 });
    dispatch(processesActions.actions.setKillProcessToasterId(toastId));
    executeCommand(pids, commandTypes.PROCESS, selectedDevice?.uid, selectedDevice?.systemDetails?.os);

    // const nonFilteredObjects = processes.filter(
    //   (object) => !selectedProcesses.includes(object.id)
    // );
    // dispatch(processesActions.actions.setProcesses(nonFilteredObjects));
  };

  const handleDeletePackages = () => {
    handleDeleteApps(
      {
        selectedApps: selectedPackages,
        filteredData: filteredAllApps,
        dispatchAction: applicationsActions.actions.setCombineApps,
        dispatch,
        selectedDevice
      }
    );
  };

  const [networkSearchTerm, setNetworkSearchTerm] = useState("");
  const [portSearchTerm, setPortSearchTerm] = useState("");
  const [packageSearchTerm, setPackageSearchTerm] = useState("");
  const [processSearchTerm, setProcessSearchTerm] = useState("");

  const handleNetworkSearch = (term) => {
    setNetworkSearchTerm(term);
  };

  const handlePortSearch = (term) => {
    setPortSearchTerm(term);
  };

  const handlePackageSearch = (term) => {
    setPackageSearchTerm(term);
  };

  const handleProcessSearch = (term) => {
    setProcessSearchTerm(term);
  };

  const filteredNetworks = useMemo(() => {
    if (!networkSearchTerm) return networks;
    const regex = new RegExp(networkSearchTerm, "i");
    return networks.filter((network) =>
      Object.values(network).some((value) => regex.test(value))
    );
  }, [networks, networkSearchTerm]);

  const filteredPorts = useMemo(() => {
    if (!portSearchTerm) return ports;
    const regex = new RegExp(portSearchTerm, "i");
    return ports.filter((port) =>
      Object.values(port)?.some((value) => regex.test(value))
    );
  }, [ports, portSearchTerm]);

  const filteredAllApps = useMemo(() => {
    if (!packageSearchTerm) return combineApps;
    const regex = new RegExp(packageSearchTerm, "i");
    return combineApps.filter((pkg) =>
      Object.values(pkg)?.some((value) => regex.test(value))
    );
  }, [combineApps, packageSearchTerm]);

  const filteredProcesses = useMemo(() => {
    if (!processSearchTerm) return processes;
    const regex = new RegExp(processSearchTerm, "i");
    return processes.filter((process) =>
      Object.values(process)?.some((value) => regex.test(value))
    );
  }, [processes, processSearchTerm]);


  const allAppColumns = useMemo(() => {
    return selectedDevice?.systemDetails?.os === OS.windows ? windowsAllAppsColumns : linuxAllAppsColumns
  }, [selectedDevice?.systemDetails?.os]);

  const allAppDownloadColumns = useMemo(() => {
    return selectedDevice?.systemDetails?.os === OS.windows ? WINDOWS_APP_DOWNLOAD_COLS : LINUX_APP_DOWNLOAD_COLS
  }, [selectedDevice?.systemDetails?.os]);

  const portColumns = getPortColumns({ isPermissionAllowed: checkSinglePermission(USER_PERMISSIONS.KILL_PORT, userPermissions, currentUser?.role) });

  return (
    <div>
      <DashboardCards isConsoleAllowed={checkSinglePermission(USER_PERMISSIONS.ACCESS_SINGLE_DEVICE_CONSOLE, userPermissions, currentUser?.role)} />
      <Grid
        my={"15px"}
        container
        sx={{ justifyContent: { xl: "space-between", md: "center" } }}
        spacing={2}
      >
        <Grid item xs={12} md={6}>
          <CheckSinglePerm allowedPerm={USER_PERMISSIONS.VIEW_DEVICE_LOCATION}>
            <DeviceMap
              markers={markers}
              center={singleDeviceLocation?.loc}
            />
          </CheckSinglePerm>
        </Grid>
        <Grid item xs={12} md={6}>
          <DetailChartCard
            details={appDetails}
            title={"Total Apps Installed"}
            chartSeries={{
              a: combineApps?.length,
              b: applications?.length,
              c: packages?.length,
            }}
            totalValue={combineApps?.length}
            graphColors={graphColors}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <CheckSinglePerm allowedPerm={USER_PERMISSIONS.VIEW_SYSTEM_DETAILS}>
          <Grid my={"15px"} item md={12}>
            <SystemInfo />
          </Grid>
        </CheckSinglePerm>
      </Grid>
      <Grid container spacing={2}>
        <CheckSinglePerm allowedPerm={USER_PERMISSIONS.VIEW_NETWORK_INTERFACES}>
          <Grid item xs={12} className={table.tableContainer}>
            <CustomTitle title={"Network Interfaces"} count={filteredNetworks?.length} />
            <Card>
              <MyTableToolbar
                placeHolder={"Search For Network"}
                onSearch={handleNetworkSearch}
                onDownload={() => handleDownloadCsv({ rows: filteredNetworks, columns: NETWORK_DOWNLOAD_COLS, name: `${selectedDevice?.deviceName}_network_interfaces` })}
                onPrint={() => handlePrintCsv({ rows: filteredNetworks, columns: NETWORK_DOWNLOAD_COLS, name: `${selectedDevice?.deviceName}_network_interfaces` })}
              />
              <MyTable
                checkboxSelection={false}
                columns={networkColumns}
                rows={filteredNetworks}
              />
            </Card>
          </Grid>
        </CheckSinglePerm>
        <CheckSinglePerm allowedPerm={USER_PERMISSIONS.VIEW_NETWORK_PORTS}>
          <Grid item xs={12} className={table.tableContainer}>
            <CustomTitle title={"Network Ports"} count={filteredPorts?.length} />
            <Card>
              <MyTableToolbar
                placeHolder={"Search For Ports"}
                onSearch={handlePortSearch}
                onDownload={() => handleDownloadCsv({ rows: filteredPorts, columns: PORT_DOWNLOAD_COLS, name: `${selectedDevice?.deviceName}_ports` })}
                onPrint={() => handlePrintCsv({ rows: filteredPorts, columns: PORT_DOWNLOAD_COLS, name: `${selectedDevice?.deviceName}_ports` })}
              />
              <MyTable
                checkboxSelection={false}
                columns={portColumns}
                rows={filteredPorts}
              />
            </Card>
          </Grid>
        </CheckSinglePerm>
        <CheckSinglePerm allowedPerm={USER_PERMISSIONS.VIEW_INSTALLED_APPLICATIONS}>
          <Grid item xs={12} className={table.tableContainer}>
            <CustomTitle title={"All Applications"} count={filteredAllApps?.length} />
            <Card>
              <MyTableToolbar
                placeHolder={"Search For Applications"}
                onSearch={handlePackageSearch}
                onDownload={() => handleDownloadCsv({ rows: filteredAllApps, columns: allAppDownloadColumns, name: `${selectedDevice?.deviceName}_all_apps` })}
                onPrint={() => handlePrintCsv({ rows: filteredAllApps, columns: allAppDownloadColumns, name: `${selectedDevice?.deviceName}_all_apps` })}
                btn1Txt={false}
                isUninstall={false}
                handleBtn2Click={handleDeletePackages}
                // btn2Txt={checkSinglePermission(USER_PERMISSIONS.UNINSTALL_APPLICATION, userPermissions, currentUser?.role) ? "Uninstall" : null}
                // btn2Icon={DeleteIcon}
              />
              <MyTable
                checkboxSelection={false}
                columns={allAppColumns}
                rows={filteredAllApps}
                handleSelectedRows={handlePackageSelection}
              />
            </Card>
          </Grid>
        </CheckSinglePerm>
        <CheckSinglePerm allowedPerm={USER_PERMISSIONS.VIEW_RUNNING_PROCESSES}>
          <Grid item xs={12} className={table.tableContainer}>
            <CustomTitle title={"Processes"} count={filteredProcesses?.length} />
            <Card>
              <MyTableToolbar
                placeHolder={"Search For Processes"}
                onSearch={handleProcessSearch}
                onDownload={() => handleDownloadCsv({ rows: filteredProcesses, columns: PROCESS_DOWNLOAD_COLS, name: `${selectedDevice?.deviceName}_processes` })}
                onPrint={() => handlePrintCsv({ rows: filteredProcesses, columns: PROCESS_DOWNLOAD_COLS, name: `${selectedDevice?.deviceName}_processes` })}
                // handleBtn2Click={handleKillProcesses}
                btn1Txt={false}
                // btn2Txt={checkSinglePermission(USER_PERMISSIONS.KILL_PROCESS, userPermissions, currentUser?.role) ? "Kill processes" : null}
                // btn2Icon={NoneIcon}
              />
              <MyTable
                checkboxSelection={false}
                columns={processesColumns}
                rows={filteredProcesses}
                handleSelectedRows={handleProcessesSelection}
              />
            </Card>
          </Grid>
        </CheckSinglePerm>
      </Grid>
      <br />
    </div>
  );
};

export default SingleDashboard;
